import { formatCurrency } from './utils/formatters.js'

/**
 * Every number the stat cards show, as pure functions (Phase 1I).
 *
 * This was 22 Vue `computed`s in `useSummaryStatisticsComputed.js`, which is a
 * `(costs, results, facilities, customers, baseline, methods) → numbers`
 * mapping wearing reactivity. The Vue composable is a thin wrapper over this
 * now, and the React cards call it directly — so the two cannot drift, and the
 * arithmetic is testable without mounting anything.
 */

export type Facility = { type?: string; facility_type?: string; capacity?: number; [k: string]: unknown }
export type Customer = { demand?: number; [k: string]: unknown }

export type OptimizationResults = {
  service_level?: number
  service_level_per_customer?: number
  demand_fulfilled?: number
  open_dcs?: number
  avg_capacity_utilization?: number
  total_capacity?: number
  units_processed?: number
  risk_score?: number
  data_quality_score?: number
  total_cost?: number
} | null

/** Costs as the cards read them: a number, or null meaning "the request failed". */
export type StoreCosts = {
  total_cost: number | null
  fixed_cost: number | null
  variable_cost: number | null
  shipping_cost: number | null
  holding_cost?: number | null
}

export type StatsInputs = {
  optimizationResults: OptimizationResults
  storeCosts: StoreCosts
  facilities: Facility[]
  customers: Customer[]
  baselineCosts: { total_cost?: number; fixed_cost?: number; shipping_cost?: number; variable_cost?: number }
  baselineResults: Record<string, unknown> | null
  capacityCalculationMethod: string
  serviceLevelCalculationMethod: string
  /** `total_demand` from the cost response, when it has one. */
  reportedTotalDemand?: number | null
}

const isType = (f: Facility, type: string) => f.type === type || f.facility_type === type

export const totalFacilities = (facilities: Facility[]) => facilities.length
export const plantCount = (facilities: Facility[]) => facilities.filter((f) => isType(f, 'plant')).length
export const dcCount = (facilities: Facility[]) => facilities.filter((f) => isType(f, 'dc')).length
export const totalCustomers = (customers: Customer[]) => customers.length

/**
 * Demand as the optimizer reports it, else summed from the customers.
 * Rounded either way: a simulation can report 205.071309999.
 */
export function totalDemand(reported: number | null | undefined, customers: Customer[]): number {
  if (reported !== undefined && reported !== null) return Math.round(reported)
  return Math.round(customers.reduce((sum, c) => sum + (c.demand || 0), 0))
}

export function totalCapacity(results: OptimizationResults, facilities: Facility[]): number {
  if (results?.total_capacity) return results.total_capacity
  return facilities.filter((f) => (f.capacity || 0) > 0).reduce((sum, f) => sum + (f.capacity || 0), 0)
}

/**
 * Two methods, and they answer different questions: 'average' is the mean
 * utilisation the optimizer reports per facility, anything else is
 * network-wide processed ÷ capacity.
 */
export function utilizationPct(inputs: StatsInputs): number {
  const { optimizationResults: results, capacityCalculationMethod } = inputs
  if (capacityCalculationMethod === 'average') return results?.avg_capacity_utilization || 0
  const processed = results?.units_processed || totalDemand(inputs.reportedTotalDemand, inputs.customers)
  const capacity = totalCapacity(results, inputs.facilities)
  return capacity === 0 ? 0 : (processed / capacity) * 100
}

export function spareCapacity(results: OptimizationResults, facilities: Facility[]): number {
  return Math.max(0, totalCapacity(results, facilities) - (results?.units_processed || 0))
}

export const openDCs = (results: OptimizationResults) => results?.open_dcs || 0
export const demandFulfilled = (results: OptimizationResults) => results?.demand_fulfilled || 0
/**
 * Network risk and data quality, or NULL when nobody computed them.
 *
 * `|| 0` until 2026-09-13, and the backend sends neither field — not from
 * /scenarios/{id}/results, before or after an optimization run. So both cards
 * showed a fabricated zero, and the two of them lied in opposite directions:
 * Network Risk read "0.0/5.0 — Low risk" in green on every network ever
 * modelled, and Data Quality read "0.0%", which is the worst possible score.
 * One falsely reassuring, one falsely alarming, both reachable from Customize
 * Map → Summary Statistics.
 *
 * Null, so `formatCardValue` renders the em dash it already has for exactly
 * this — the Cost of Averaging card uses the same convention, and its comment
 * says why: "$0 says uncertainty costs you nothing, while null says nobody
 * checked". A score nobody computed is the same shape of claim.
 */
export const networkRisk = (results: OptimizationResults): number | null =>
  typeof results?.risk_score === 'number' ? results.risk_score : null
export const dataQuality = (results: OptimizationResults): number | null =>
  typeof results?.data_quality_score === 'number' ? results.data_quality_score : null

export function serviceLevel(results: OptimizationResults, method: string): number {
  return method === 'volume_weighted'
    ? results?.service_level || 0
    : results?.service_level_per_customer || 0
}

export type CostDelta = { amount: number; percent: number; direction: 'up' | 'down' | 'none' }

/**
 * The change in total cost from the baseline — ONE signed quantity.
 *
 * `amount` and `percent` share a sign: positive means it costs MORE than the
 * baseline. They used to disagree by construction, so a saving rendered as
 * "$2.0 M (-10%)": an unsigned figure beside a negative one, for one event.
 *
 * null when there is nothing to say — no baseline, or a current cost of null,
 * which means the request failed and the card is showing an em dash.
 */
export function costDelta(currentTotal: number | null, baselineTotal: number | null | undefined): CostDelta | null {
  if (currentTotal == null) return null
  const baseline = baselineTotal || 0
  if (!baseline) return null
  const amount = currentTotal - baseline
  return {
    amount,
    percent: (amount / baseline) * 100,
    // Zero is its own case: neither good nor bad. It used to paint red with an
    // up arrow because `< 0` was false.
    direction: amount > 0 ? 'up' : amount < 0 ? 'down' : 'none',
  }
}

/** The signed amount as shown, in the cards' currency format. */
export function formatCostDelta(delta: CostDelta | null): string | null {
  if (!delta) return null
  return `${delta.amount > 0 ? '+' : ''}${formatCurrency(delta.amount)}`
}

/** The signed percent as shown: never "0%" for a change that is not zero. */
export function formatCostDeltaPercent(delta: CostDelta | null): string | null {
  if (!delta) return null
  if (delta.amount === 0) return '0%'
  const rounded = Math.round(delta.percent)
  if (rounded === 0) return delta.percent > 0 ? '+<1%' : '-<1%'
  return `${rounded > 0 ? '+' : ''}${rounded}%`
}

/** The (default-hidden) Baseline Delta card's raw figure. */
export function baselineDeltaValue(
  currentTotal: number | null,
  baselineTotal: number | undefined,
  baselineResults: Record<string, unknown> | null,
): number {
  if (!baselineResults || !baselineTotal) return 0
  return (currentTotal || 0) - baselineTotal
}

/** Everything the card catalogue needs, derived once. */
export function computeStats(inputs: StatsInputs) {
  const { optimizationResults: results, storeCosts, facilities, customers } = inputs
  return {
    totalFacilities: totalFacilities(facilities),
    plantCount: plantCount(facilities),
    dcCount: dcCount(facilities),
    totalCustomers: totalCustomers(customers),
    totalDemand: totalDemand(inputs.reportedTotalDemand, customers),
    totalCapacity: totalCapacity(results, facilities),
    utilizationPct: utilizationPct(inputs),
    spareCapacity: spareCapacity(results, facilities),
    openDCs: openDCs(results),
    serviceLevel: serviceLevel(results, inputs.serviceLevelCalculationMethod),
    demandFulfilled: demandFulfilled(results),
    networkRisk: networkRisk(results),
    dataQuality: dataQuality(results),
    // Chart inputs, in the shape the builders expect.
    fixedCostValue: storeCosts.fixed_cost || 0,
    transportCostValue: storeCosts.shipping_cost || 0,
    variableCostValue: storeCosts.variable_cost || 0,
    totalCostValue: storeCosts.total_cost || 0,
    baselineDeltaValue: baselineDeltaValue(storeCosts.total_cost, inputs.baselineCosts.total_cost, inputs.baselineResults),
  }
}
